// One catch: photos, facts, the weather at that hour, the spot on a map, share, edit and delete.
import { dbDel } from "./db.ts";
import { $, el, icon, toast, type Child } from "./dom.ts";
import { fmtNum, fmtWhen, methodLabel, nameOf, t, windFrom } from "./i18n.ts";
import { collapseMini, makeExpandable } from "./map/expand.ts";
import { hasLeaflet, layerName, pinIcon, tileLayer, withDepth, type CentredMap } from "./map/layers.ts";
import { dropPhotoUrls, photoUrl } from "./photos.ts";
import { speciesName } from "./species.ts";
import { catches, render, setCatches } from "./state.ts";
import { hasWater, waterSource } from "./water.ts";
import { moon, pressureTrend } from "./weather.ts";
import { openEditor } from "./editor.ts";
import { shareCatch } from "./groups/share.ts";
import { openOmaRiista } from "./omariista.ts";

const dlg = $<HTMLDialogElement>("#detail");
dlg.addEventListener("cancel", (e) => { if (collapseMini) { e.preventDefault(); collapseMini(); } });
let shownId: string | null = null, detailMap: L.Map | null = null;

/** A label and value in the facts grid; nothing when there is no value. */
function fact(label: string, value: Child, small?: string): HTMLElement | null {
  return value == null || value === "" ? null : el("div", {}, el("dt", { text: label }), el("dd", {}, value, small ? el("small", { text: " " + small }) : null));
}

export function openDetail(id: string): void {
  const c = catches.find((x) => x.id === id);
  if (!c) return;
  shownId = id;
  disarmDelete();
  const name = speciesName(c);
  $("#d-title").textContent = name;
  const gallery = $("#d-gallery");
  if (c.photoIds?.length) {
    gallery.replaceChildren(...c.photoIds.map((pid) => { const i = el("img", { alt: t("photoAlt") }); void photoUrl(pid, "full").then((u) => { if (u) i.src = u; }); return i; }));
  } else gallery.replaceChildren(el("div", { class: "nophoto" }, icon(c.kind)));

  const w = c.weather, mo = moon(c.time);
  const catchFacts = el("dl", { class: "facts" },
    fact(t("when"), fmtWhen(c.time)),
    fact(t("weight"), c.weightKg != null ? fmtNum(c.weightKg, 2) + " kg" : null),
    c.kind === "fish" ? fact(t("length"), c.lengthCm != null ? fmtNum(c.lengthCm) + " cm" : null) : null,
    fact(t("method"), c.method ? methodLabel(c.method) : null),
    fact(t(c.kind === "fish" ? "gearFish" : "gearGame"), c.gear),
    fact(t("place"), c.place),
    hasWater(c) ? fact(t("waterTemp"), fmtNum(c.water.value, 1) + " °C", waterSource(c.water)) : null);
  let wx: HTMLElement;
  if (!c.loc) wx = el("p", { class: "hint", text: t("wxNoSpot") });
  else if (!w || w.status !== "ok") wx = el("p", { class: "hint warn", text: navigator.onLine ? t("wxPendingLong") : t("wxOfflineLong") });
  else {
    const arrow = w.windDir != null ? el("span", { class: "arrow", "aria-hidden": "true", style: `transform:rotate(${w.windDir + 180}deg)`, text: "↑" }) : null;
    const d3 = w.pressure3h != null ? (w.pressure3h > 0 ? "+" : "") + fmtNum(w.pressure3h) : null;
    wx = el("dl", { class: "facts" },
      fact(t("weather"), (w.code != null ? nameOf("wmo", w.code) : null) ?? "–", w.temp != null ? Math.round(w.temp) + "°C" : ""),
      w.wind != null ? el("div", {}, el("dt", { text: t("wind") }), el("dd", {}, fmtNum(w.wind, 1) + " m/s ", w.windDir != null ? windFrom(w.windDir) + " " : "", arrow,
        w.gust != null ? el("small", { text: " " + t("gusts", fmtNum(w.gust, 0)) }) : null)) : null,
      fact(t("pressure"), w.pressure != null ? Math.round(w.pressure) + " hPa" : null, d3 ? t("trend3h", pressureTrend(w.pressure3h), d3) : ""),
      fact(t("cloud"), w.cloud != null ? Math.round(w.cloud) + " %" : null),
      fact(t("rain"), w.precip != null ? fmtNum(w.precip, 1) + " mm/h" : null),
      fact(t("humidity"), w.humidity != null ? Math.round(w.humidity) + " %" : null),
      fact(t("moon"), mo.name, t("moonLit", mo.illum)));
  }
  const moonFacts = w && w.status === "ok" ? null : el("dl", { class: "facts" }, fact(t("moon"), mo.name, t("moonLit", mo.illum)));

  const tags = el("div", { class: "dtitle" }, el("span", { class: "tag " + c.kind, text: t(c.kind) }),
    c.kind === "fish" && c.released ? el("span", { class: "tag rel", text: t("released") }) : null);
  const loc = c.loc;
  const mapBox = loc ? el("div", { class: "minimap", id: "d-map" }) : null;
  const mapWrap = mapBox ? el("div", { class: "mapbox" }, mapBox) : null;
  const parts: (HTMLElement | null)[] = [tags, catchFacts,
    el("div", { class: "sect" }, el("h3", { text: t("wxHeading") }), wx, moonFacts),
    loc ? el("div", { class: "sect" }, el("h3", { text: t("spot") }), mapWrap,
      el("div", { class: "coords num", text: `${loc.lat.toFixed(5)}, ${loc.lon.toFixed(5)}${loc.acc ? ` · ±${loc.acc} m` : ""}` }),
      el("a", { class: "btn small", href: `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lon}`, target: "_blank", rel: "noopener", style: "align-self:flex-start" }, t("directions"))) : null,
    c.notes ? el("div", { class: "sect" }, el("h3", { text: t("notes") }), el("p", { class: "notes", text: c.notes })) : null,
    c.kind === "game" ? el("div", { class: "sect" }, el("h3", { text: "Oma riista" }), el("p", { class: "hint", text: t("omaRiistaHint") }),
      el("button", { class: "btn", type: "button", style: "align-self:flex-start", onclick: () => void openOmaRiista() },
        icon("external"), el("span", { text: t("omaRiistaOpen") }))) : null,
  ];
  $("#d-body").replaceChildren(...parts.filter((x): x is HTMLElement => x != null));
  if (!dlg.open) dlg.showModal();
  $("#detail .sheet-body").scrollTop = 0;
  detailMap?.remove();
  detailMap = null;
  if (loc && mapBox && mapWrap && hasLeaflet()) {
    const map: CentredMap = L.map(mapBox, { zoomControl: true, attributionControl: false, dragging: true, scrollWheelZoom: false }).setView([loc.lat, loc.lon], 13);
    tileLayer(layerName).addTo(map);
    withDepth(map);
    L.marker([loc.lat, loc.lon], { icon: pinIcon(c, null) }).addTo(map);
    map.centre = () => [loc.lat, loc.lon];
    makeExpandable(mapWrap, () => map, () => `${name} · ${fmtWhen(c.time)}`);
    detailMap = map;
    requestAnimationFrame(() => map.invalidateSize());
  }
}
export function closeDetail(): void {
  if (dlg.open) dlg.close();
}

$("#detail-close").addEventListener("click", () => { collapseMini?.(); dlg.close(); });
$("#d-share").addEventListener("click", () => { const c = catches.find((x) => x.id === shownId); if (c) void shareCatch(c); });
$("#d-edit").addEventListener("click", () => { const c = catches.find((x) => x.id === shownId); if (c) openEditor(c); });

// Delete takes two taps.
let delTimer = 0;
function disarmDelete(): void {
  clearTimeout(delTimer);
  const b = $("#d-delete");
  b.classList.remove("armed");
  b.textContent = t("del");
}
$("#d-delete").addEventListener("click", async () => {
  const b = $("#d-delete");
  if (!b.classList.contains("armed")) { b.classList.add("armed"); b.textContent = t("delConfirm"); delTimer = window.setTimeout(disarmDelete, 3500); return; }
  disarmDelete();
  const c = catches.find((x) => x.id === shownId);
  if (!c) return;
  try {
    for (const pid of c.photoIds ?? []) { await dbDel("photos", pid); dropPhotoUrls(pid); }
    await dbDel("catches", c.id);
    setCatches(catches.filter((x) => x.id !== c.id));
    dlg.close(); render(); toast(t("deleted"));
  } catch { toast(t("deleteFailed")); }
});
